/**
 * Does this account-plane ticker MAYBE identify a player-prop market?
 *
 * The consumer feed filter excludes `player-prop` rows by default; a trader
 * holding a prop position (or a resting prop order) would then have no live
 * row for it — no mark price, no P&L, no book — until a prop-rendering view
 * happened to be open. The account plane therefore opts the feed back into
 * the class whenever any of its tickers may be a prop. Fail-open toward
 * data: a ticker g1's parser cannot place in a known NON-prop family counts
 * as a possible prop.
 *
 * The parser's fail-open is not enough on its own: a sport-wide catch-all
 * prefix PLACES a prop in a non-prop family — every `KXNBA…` prop series
 * parses as `nba-season` through the bare `KXNBA` season dispatch, so a held
 * NBA prop read as "not a prop" (verified 2026-10-21, `KXNBAPTS-…` →
 * `nba-season`). The prop vocabulary's exact-series table is therefore
 * consulted FIRST, for every sport that lists one.
 */
import { parseKalshiTicker } from '@/lib/kalshiTicker/parse';
import { classifyPolyExtendedTicker } from '@/lib/kalshiTicker/polyPropsEvents';
import { MLB_PROP_CATEGORIES } from '@/lib/kalshiTicker/sports/mlb';
import { NFL_PROP_CATEGORIES } from '@/lib/kalshiTicker/sports/nfl';
import { propStatOfKalshiSeries } from '@/lib/sport/propStat';
import type { TickerCategory } from '@/lib/kalshiTicker/types';

/** Parser categories that ARE player props: the MLB ladders and every NFL
 *  per-game player series (stat ladders + the touchdown families). Read off
 *  the two sport registries so a new series cannot be missed here. */
const PLAYER_PROP_CATEGORIES: ReadonlySet<TickerCategory> = new Set<TickerCategory>([
  ...Object.values(MLB_PROP_CATEGORIES),
  ...Object.values(NFL_PROP_CATEGORIES),
]);

export function tickerMayBePlayerProp(ticker: string): boolean {
  if (!ticker) return false;
  if (ticker.startsWith('PM-')) {
    return classifyPolyExtendedTicker(ticker)?.kind === 'prop';
  }
  // A catalogued prop series (NFL, NHL, NBA — lib/sport/propStat), before
  // the parser can file it under a sport's catch-all.
  if (propStatOfKalshiSeries(ticker)) return true;
  const parsed = parseKalshiTicker(ticker);
  if (PLAYER_PROP_CATEGORIES.has(parsed.category)) return true;
  // Unknown Kalshi family: the parser has no opinion, so neither do we —
  // fail open (a false positive costs egress; a false negative costs a
  // trader their mark).
  return parsed.category === 'unknown';
}
